import { beforeTimeoutEvent } from '../eventBus/appEventTypes';
import { eventBus } from '../internal';
import { getLogger } from '../logging';
import { getPreference, setPreferences } from '../services';
import ApplicationSwitcherClient from './ApplicationSwitcherClient';
import ApplicationSwitcherMain from './ApplicationSwitcherMain';
import { enableAppSwitcher } from './ApplicationSwitcherStatus';
import { CREATE, HIDE, OPEN, PREFERENCE_CHANGED, SHOW, UPDATE_MODEL } from './events/ApplicationSwitcherObservable';
import AppRegistryService from './services/AppRegistryService';
class ApplicationSwitcher {
  constructor(config) {
    this.config = config;
    if (window === top?.window) {
      new ApplicationSwitcherMain(config);
    }
    this.client = new ApplicationSwitcherClient(config);
  }
  broadcastMessage(type, data = {}) {
    this.client.broadcastMessage(type, data);
  }
  getAvailableApps() {
    return this.client.getModel().availableApplications;
  }
  async updateFavorites(directives) {
    await this.config.applicationService.updateFavorites(directives);
    // Update this and other appswitchers' favorites.
    await this.client.updateModel();
    this.client.broadcastMessage(UPDATE_MODEL);
  }
  async addFavorite(favoriteUri) {
    await this.config.applicationService.addFavorite(favoriteUri);
    // Update this and other appswitchers' favorites.
    await this.client.updateModel();
    this.client.broadcastMessage(UPDATE_MODEL);
  }
  async removeFavorite(favoriteUri) {
    await this.config.applicationService.removeFavorite(favoriteUri);
    // Update this and other appswitchers' favorites.
    await this.client.updateModel();
    this.client.broadcastMessage(UPDATE_MODEL);
  }
  getPublicTypes() {
    return this.client.getModel().publicTypeSupportingApplications;
  }
  gotoApp(appId, url, isCompliant, options) {
    this.client.gotoApp(appId, url, isCompliant, options);
  }
  logoff() {
    this.client.logoff();
  }
  sendMessageToApp(appId, type, data) {
    this.client.sendMessageToApp(appId, type, data);
  }
  setDirtyMessageEnabled(allowDirtyMessage) {
    this.client.setDirtyMessageEnabled(allowDirtyMessage);
  }
  setLogoffUrl(logoffUrl) {
    this.client.setLogoffUrl(logoffUrl);
  }
  subscribe(type, fn, context) {
    this.client.subscribe(type, fn, context);
  }
  timeout(url) {
    this.client.timeout(url);
  }
  unsubscribe(type, fn, context) {
    this.client.unsubscribe(type, fn, context);
  }
  updateAppName(appId, appName) {
    this.client.updateAppName(appId, appName);
  }
  updateDirtyFlag(appId, dirtyFlag) {
    this.client.updateDirtyFlag(appId, dirtyFlag);
  }
  registerApp(appId, url, appName) {
    this.client.registerApp(appId, url, appName);
  }
  openObject(objectType, objectInfo, preferredAppId) {
    this.client.openObject(objectType, objectInfo, OPEN, preferredAppId);
  }
  createObject(objectType, objectInfo, preferredAppId) {
    this.client.openObject(objectType, objectInfo, CREATE, preferredAppId);
  }
  updateModel() {
    this.client.updateModel();
  }
  setUnsavedChangesLocalizedFn(fn) {
    this.config.getUnsavedChangesLocalizedString = fn;
  }
  getAvailableApp(appId) {
    return this.client.getAvailableApp(appId);
  }
}
function isAppData(data) {
  return data && typeof data === 'object' && 'appId' in data;
}
function saveLastAppDirective(currentAppId) {
  setPreferences([{
    id: 'sas.drive.logon.lastAppDirective',
    value: currentAppId
  }]).catch(_error => getLogger('appSwitcher').error(`set preference sas.drive.logon.lastAppDirective to ${currentAppId} failed.`));
}
export const createApplicationSwitcher = config => {
  const applicationSwitcher = new ApplicationSwitcher(config);
  let currentAppId;
  let trackLastAppDirective;
  enableAppSwitcher();

  // tackle change in lastAppDirective preference
  applicationSwitcher.subscribe(PREFERENCE_CHANGED, (_, data) => {
    if (data?.settings) {
      for (const {
        id,
        newValue
      } of data.settings) {
        const isLogonAction = id === 'sas.drive.logon.action';
        const isSpecificAppDirective = id === 'sas.drive.logon.specificAppDirective';

        // We have to write this odd condition to check specificAppDirective to tackle
        // the wrong preference broadcasted through appSwitcher by the ui5 drive app code
        // on the client side.
        if (isLogonAction || isSpecificAppDirective) {
          trackLastAppDirective = isLogonAction && newValue === 'lastAppDirective' || isSpecificAppDirective && newValue === 'lastAppDirective';
          const isAppAtTop = data.broadcaster?.appId && data.broadcaster.appId === currentAppId;
          if (trackLastAppDirective && currentAppId && isAppAtTop) {
            saveLastAppDirective(currentAppId);
          }
          break; // Exit the loop early since we've found our condition
        }
      }
    }
  });

  // fire up show event on event bus and check if lastAppDirective needs tracking.
  applicationSwitcher.subscribe(SHOW, (_type, data) => {
    eventBus.publish(SHOW, {
      data
    });
    if (isAppData(data) && typeof data.appId === 'string') {
      currentAppId = data.appId;
      if (trackLastAppDirective === true) {
        saveLastAppDirective(currentAppId);
      } else if (trackLastAppDirective === undefined) {
        getPreference('sas.drive.logon.action').then(logonAction => {
          // double check just incase the preference change ran earlier. (Not sure it can happen though)
          if (trackLastAppDirective === undefined) {
            if (logonAction.value === 'lastAppDirective' && currentAppId) {
              trackLastAppDirective = true;
              saveLastAppDirective(currentAppId);
            } else {
              trackLastAppDirective = false;
            }
          }
        }).catch(_error => {
          // do nothing
        });
      }
    }
  });

  // Clean up cache when an application is hidden
  applicationSwitcher.subscribe(HIDE, (_, data) => {
    currentAppId = undefined;
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    eventBus.publish('Cache.Reload', {
      data
    });
  });

  // TODO fire other app switcher events on the bus. Rob?
  eventBus.subscribe(beforeTimeoutEvent, () => {
    applicationSwitcher.setDirtyMessageEnabled(false);
  });
  return applicationSwitcher;
};
const getLogoffUrl = () => {
  const origin = window.location.origin;
  const pathname = window.location.pathname;
  const secondSlashIndex = pathname.indexOf('/', 1);
  const contextPath = pathname.substring(0, secondSlashIndex);
  const logoutPath = '/logout';
  return origin + contextPath + logoutPath;
};
let applicationSwitcher;
export const getApplicationSwitcher = () => {
  if (!applicationSwitcher) {
    applicationSwitcher = createApplicationSwitcher({
      applicationService: new AppRegistryService(),
      getLogoffUrl,
      getUnsavedChangesLocalizedString: () => {
        return '';
      }
    });
  }
  return applicationSwitcher;
};
export const setApplicationSwitcher = customApplicationSwitcher => {
  applicationSwitcher = customApplicationSwitcher;
};
export default ApplicationSwitcher;
//# sourceMappingURL=ApplicationSwitcher.js.map